import { Separator } from '@sea-snag/ui';
import { createFileRoute, Link } from '@tanstack/react-router';

import { LandingFooter, LandingHeader } from '../components/landing';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <LandingHeader />
      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-4xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: February 16, 2026</p>

        <Separator className="my-8" />

        <div className="space-y-8 text-muted-foreground [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mt-4 [&_h3]:text-base [&_h3]:font-medium [&_h3]:text-foreground [&_p]:mt-3 [&_p]:leading-relaxed [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Sea Snag (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) operates the Sea Snag
              platform at seasnag.com, a software-as-a-service solution for yacht charter agency
              management. This Privacy Policy describes how we collect, use, share, and protect your
              personal information when you visit our website, create an account, or use any of our
              services.
            </p>
            <p>
              By using the Sea Snag platform, you consent to the practices described in this Privacy
              Policy. If you do not agree with this policy, please do not use our services. This
              policy should be read together with our{' '}
              <Link to="/terms" className="text-foreground underline">
                Terms of Service
              </Link>
              .
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide</h3>
            <ul>
              <li>
                <strong>Account &amp; Profile Information:</strong> When you register, we collect
                your full name, email address, and password. Organization owners also provide their
                company name, business address, and contact details.
              </li>
              <li>
                <strong>Fleet Data:</strong> Yacht specifications, photos, descriptions,
                availability calendars, base ports, and equipment inventories that you enter to
                manage your charter fleet.
              </li>
              <li>
                <strong>Fleet &amp; Operations Records:</strong> Charter availability details
                including travel dates, itineraries, and any operational information you store on
                the platform.
              </li>
              <li>
                <strong>Financial Information:</strong> Pricing configurations, revenue records,
                commission structures, and payment details. Credit card and bank information is
                processed directly by Stripe and is never stored on our servers.
              </li>
              <li>
                <strong>Messages &amp; Communications:</strong> Content of messages sent through our
                in-platform messaging system between organization members, as well as any emails or
                support requests you send to us.
              </li>
              <li>
                <strong>Team &amp; Access Data:</strong> Information about organization members you
                invite, including their names, email addresses, assigned roles, and permission
                configurations.
              </li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <ul>
              <li>
                <strong>Device &amp; Browser Information:</strong> IP address, browser type and
                version, operating system, device type, screen resolution, and language preferences.
              </li>
              <li>
                <strong>Usage Analytics:</strong> Pages visited, features used, click patterns, time
                spent on pages, navigation paths, search queries within the platform, and feature
                adoption metrics.
              </li>
              <li>
                <strong>Performance Data:</strong> Page load times, error logs, crash reports, and
                API response times collected through our monitoring tools (Sentry) to maintain
                service quality.
              </li>
              <li>
                <strong>Session Information:</strong> Login timestamps, session duration, and
                authentication events for security monitoring.
              </li>
            </ul>

            <h3>2.3 Information from Third Parties</h3>
            <ul>
              <li>
                <strong>Payment Processor:</strong> Stripe provides us with transaction
                confirmations, payment status updates, and subscription state, but never shares your
                full card details with us.
              </li>
              <li>
                <strong>Authentication Providers:</strong> If you use social login options, we
                receive your name and email from the identity provider you choose.
              </li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>

            <h3>3.1 Providing &amp; Operating the Service</h3>
            <ul>
              <li>Create and manage your account and organization.</li>
              <li>
                Enable fleet management, availability tracking, and customer relationship features.
              </li>
              <li>Coordinate fleet availability and manage charter operations.</li>
              <li>Facilitate in-platform messaging between organization members.</li>
              <li>Handle subscription billing and payment processing through Stripe.</li>
              <li>
                Deliver email notifications for pricing changes, messages, team invitations, and
                account activity.
              </li>
            </ul>

            <h3>3.2 Improving &amp; Personalizing the Service</h3>
            <ul>
              <li>
                Generate analytics dashboards and business insights from your fleet and operations
                data.
              </li>
              <li>
                Power dynamic pricing recommendations based on your historical occupancy patterns
                and market data.
              </li>
              <li>Provide competitive market intelligence and destination trend analytics.</li>
              <li>Identify and fix bugs, performance issues, and service disruptions.</li>
              <li>
                Develop new features and improve existing ones based on aggregated usage patterns.
              </li>
            </ul>

            <h3>3.3 Security &amp; Compliance</h3>
            <ul>
              <li>Detect and prevent fraud, abuse, and unauthorized access to accounts.</li>
              <li>Monitor for suspicious activity and enforce rate limits.</li>
              <li>Comply with legal obligations, tax reporting, and regulatory requirements.</li>
              <li>Respond to legal requests and protect our legal rights.</li>
            </ul>

            <h3>3.4 Communications</h3>
            <ul>
              <li>
                Send transactional emails (pricing notifications, password resets, team
                invitations).
              </li>
              <li>
                Deliver service announcements about new features, maintenance windows, and policy
                updates.
              </li>
              <li>Respond to your support inquiries and provide customer assistance.</li>
            </ul>
          </section>

          <section>
            <h2>4. Legal Basis for Processing (GDPR)</h2>
            <p>
              If you are located in the European Economic Area (EEA), United Kingdom, or
              Switzerland, we process your personal data under the following legal bases:
            </p>
            <ul>
              <li>
                <strong>Contract Performance:</strong> Processing necessary to provide the services
                you have subscribed to, including account management and fleet operations.
              </li>
              <li>
                <strong>Legitimate Interests:</strong> Processing for service improvement, security
                monitoring, fraud prevention, and analytics, where these interests are not
                overridden by your data protection rights.
              </li>
              <li>
                <strong>Legal Obligation:</strong> Processing required to comply with applicable
                laws, such as tax regulations, anti-money laundering requirements, and responses to
                lawful government requests.
              </li>
              <li>
                <strong>Consent:</strong> Where we rely on your consent for specific processing
                activities, such as optional marketing communications. You may withdraw consent at
                any time.
              </li>
            </ul>
          </section>

          <section>
            <h2>5. Information Sharing &amp; Disclosure</h2>
            <p>
              We do not sell, rent, or trade your personal information to third parties. We may
              share your information only in the following limited circumstances:
            </p>
            <ul>
              <li>
                <strong>Service Providers:</strong> We engage trusted third-party companies to help
                us operate the platform. These include DigitalOcean (cloud hosting and file
                storage), Stripe (payment processing), Sentry (error monitoring), and email delivery
                services. These providers are contractually obligated to use your data only for the
                services they provide to us.
              </li>
              <li>
                <strong>Within Your Organization:</strong> Data you enter into the platform (fleet
                details, availability, messages) is accessible to other members of your organization
                based on the roles and permissions configured by your organization owner.
              </li>
              <li>
                <strong>Partner API Access:</strong> If your organization uses our Partner API to
                share fleet availability or operations data with external platforms, that data is
                shared according to the API access you configure. You control what data is exposed.
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose your information when required
                by law, subpoena, court order, or government regulation, or when we believe
                disclosure is necessary to protect our rights, your safety, or the safety of others.
              </li>
              <li>
                <strong>Business Transfers:</strong> In the event of a merger, acquisition,
                reorganization, bankruptcy, or sale of all or a portion of our assets, your
                information may be transferred. We will notify you of any such change and any
                choices you may have regarding your information.
              </li>
            </ul>
          </section>

          <section>
            <h2>6. Data Security</h2>
            <p>
              We take the security of your data seriously and implement industry-standard measures
              to protect it:
            </p>
            <ul>
              <li>
                <strong>Encryption:</strong> All data transmitted between your browser and our
                servers is encrypted using TLS 1.2 or higher. Sensitive data at rest is encrypted
                using AES-256 encryption.
              </li>
              <li>
                <strong>Access Controls:</strong> Our internal systems use role-based access
                controls. Employee access to customer data is limited to those who require it for
                their job function and is subject to audit logging.
              </li>
              <li>
                <strong>Authentication:</strong> Passwords are hashed using bcrypt with appropriate
                salt rounds. We support secure session management with automatic expiration and
                refresh token rotation.
              </li>
              <li>
                <strong>Infrastructure:</strong> Our servers are hosted in DigitalOcean data centers
                with physical security, network firewalls, and regular security patching.
              </li>
              <li>
                <strong>Monitoring:</strong> We use automated monitoring and alerting for security
                events, failed authentication attempts, and abnormal access patterns.
              </li>
            </ul>
            <p>
              While we strive to protect your data, no method of electronic transmission or storage
              is 100% secure. We cannot guarantee absolute security, but we commit to promptly
              notifying affected users in the event of a data breach as required by applicable law.
            </p>
          </section>

          <section>
            <h2>7. Data Retention</h2>
            <ul>
              <li>
                <strong>Active Accounts:</strong> We retain your data for as long as your account is
                active and you maintain a subscription with us.
              </li>
              <li>
                <strong>After Account Deletion:</strong> When you request account deletion, we
                remove your personal data within 30 days. Certain data may be retained for up to 90
                days in encrypted backups before being permanently purged.
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may retain certain records (transaction
                history, billing records) for up to 7 years as required by tax and financial
                regulations.
              </li>
              <li>
                <strong>Anonymized Data:</strong> We may retain anonymized, aggregated data
                indefinitely for statistical analysis and service improvement. This data cannot be
                used to identify you.
              </li>
            </ul>
          </section>

          <section>
            <h2>8. Your Privacy Rights</h2>

            <h3>8.1 General Rights</h3>
            <p>
              Regardless of your location, you can manage your account information through the
              platform settings. You may update your profile, change your password, and manage
              notification preferences at any time.
            </p>

            <h3>8.2 European Economic Area, UK &amp; Switzerland (GDPR)</h3>
            <p>If you are in the EEA, UK, or Switzerland, you have the right to:</p>
            <ul>
              <li>
                <strong>Access:</strong> Request a copy of the personal data we hold about you.
              </li>
              <li>
                <strong>Rectification:</strong> Request correction of inaccurate or incomplete
                personal data.
              </li>
              <li>
                <strong>Erasure:</strong> Request deletion of your personal data (&quot;right to be
                forgotten&quot;).
              </li>
              <li>
                <strong>Restriction:</strong> Request that we limit processing of your data in
                certain circumstances.
              </li>
              <li>
                <strong>Portability:</strong> Receive your data in a structured, commonly used,
                machine-readable format (JSON or CSV).
              </li>
              <li>
                <strong>Objection:</strong> Object to processing based on legitimate interests.
              </li>
              <li>
                <strong>Withdraw Consent:</strong> Where processing is based on consent, withdraw it
                at any time without affecting prior processing.
              </li>
              <li>
                <strong>Lodge a Complaint:</strong> File a complaint with your local data protection
                supervisory authority.
              </li>
            </ul>

            <h3>8.3 California Residents (CCPA/CPRA)</h3>
            <p>If you are a California resident, you additionally have the right to:</p>
            <ul>
              <li>Know what personal information we collect, use, and disclose.</li>
              <li>Request deletion of your personal information.</li>
              <li>Opt out of the sale or sharing of personal information (we do not sell data).</li>
              <li>Non-discrimination for exercising your privacy rights.</li>
            </ul>

            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:privacy@seasnag.com" className="text-foreground underline">
                privacy@seasnag.com
              </a>
              . We will respond to verified requests within 30 days. We may request additional
              information to verify your identity before processing your request.
            </p>
          </section>

          <section>
            <h2>9. Cookies &amp; Tracking Technologies</h2>

            <h3>9.1 Essential Cookies</h3>
            <p>
              We use strictly necessary cookies to authenticate your session, maintain your login
              state, and remember your preferences (such as language and theme settings). These
              cookies are required for the platform to function and cannot be disabled.
            </p>

            <h3>9.2 Analytics</h3>
            <p>
              We use Sentry for error tracking and performance monitoring. Sentry collects technical
              data such as error stack traces, browser information, and page load metrics to help us
              identify and fix issues. This data is not used for advertising.
            </p>

            <h3>9.3 No Advertising Cookies</h3>
            <p>
              We do not use advertising cookies, tracking pixels, or retargeting technologies. We do
              not participate in ad networks or share your browsing behavior with advertisers.
            </p>

            <h3>9.4 Managing Cookies</h3>
            <p>
              You can control cookies through your browser settings. Note that disabling essential
              cookies will prevent you from logging in and using the platform.
            </p>
          </section>

          <section>
            <h2>10. International Data Transfers</h2>
            <p>
              Our servers are hosted in data centers that may be located outside your country of
              residence. If you are located in the EEA, UK, or Switzerland, your data may be
              transferred to countries that do not provide the same level of data protection. In
              such cases, we rely on appropriate safeguards including Standard Contractual Clauses
              (SCCs) approved by the European Commission, and we ensure our service providers
              maintain adequate security measures.
            </p>
          </section>

          <section>
            <h2>11. Children&apos;s Privacy</h2>
            <p>
              The Sea Snag platform is designed for business use and is not intended for individuals
              under the age of 18. We do not knowingly collect personal information from children.
              If we become aware that we have collected data from a child under 18, we will take
              steps to delete that information promptly. If you believe a child has provided us with
              personal information, please contact us at{' '}
              <a href="mailto:privacy@seasnag.com" className="text-foreground underline">
                privacy@seasnag.com
              </a>
              .
            </p>
          </section>

          <section>
            <h2>12. Third-Party Services</h2>
            <p>
              Our platform integrates with third-party services that have their own privacy
              policies. We encourage you to review their policies:
            </p>
            <ul>
              <li>
                <strong>Stripe</strong> — Payment processing (
                <a
                  href="https://stripe.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground underline"
                >
                  Privacy Policy
                </a>
                )
              </li>
              <li>
                <strong>DigitalOcean</strong> — Cloud hosting and file storage (
                <a
                  href="https://www.digitalocean.com/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground underline"
                >
                  Privacy Policy
                </a>
                )
              </li>
              <li>
                <strong>Sentry</strong> — Error tracking and performance monitoring (
                <a
                  href="https://sentry.io/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground underline"
                >
                  Privacy Policy
                </a>
                )
              </li>
            </ul>
          </section>

          <section>
            <h2>13. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our
              practices, technology, legal requirements, or other factors. When we make material
              changes, we will:
            </p>
            <ul>
              <li>Update the &quot;Last updated&quot; date at the top of this page.</li>
              <li>
                Send an email notification to the address associated with your account for
                significant changes.
              </li>
              <li>Display a prominent notice within the platform when you next log in.</li>
            </ul>
            <p>
              Your continued use of the platform after changes are posted constitutes your
              acceptance of the updated policy. We encourage you to review this page periodically.
            </p>
          </section>

          <section>
            <h2>14. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data
              practices, please contact us:
            </p>
            <ul className="!list-none !pl-0">
              <li>
                Email:{' '}
                <a href="mailto:privacy@seasnag.com" className="text-foreground underline">
                  privacy@seasnag.com
                </a>
              </li>
              <li>
                General inquiries:{' '}
                <a href="mailto:support@seasnag.com" className="text-foreground underline">
                  support@seasnag.com
                </a>
              </li>
            </ul>
            <p>We aim to respond to all privacy-related inquiries within 30 days.</p>
          </section>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
